import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  SelectChangeEvent,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import { fetchCompanyList, fetchToken, Company } from '../services/auth';
import { jwtDecode } from 'jwt-decode';
import { useUser } from '../contexts/UserContext';

interface DecodedToken {
  unique_name: string;
  UserNo: string;
  CompanyNo: string;
  MemberNo: string;
  nbf: number;
  exp: number;
  iat: number;
}

const Login = () => {
  const navigate = useNavigate();
  const { fetchUserInfo, fetchCompanyInfo } = useUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectOpen, setSelectOpen] = useState(false);

  const handleCompanySelectOpen = async () => {
    if (!email || !password) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      const companyList = await fetchCompanyList(email, password);
      setCompanies(companyList);
      if (companyList.length === 0) {
        setError('회사 정보를 찾을 수 없습니다.');
      }
      setSelectOpen(true);
    } catch (err) {
      setError('이메일 또는 비밀번호가 올바르지 않습니다.');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    setCompany(event.target.value);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) {
      setError('로그인할 업체를 선택해주세요.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // 회사 목록 조회
      const companies = await fetchCompanyList(email, password);
      const selectedCompany = companies.find(comp => comp.cust_no === company);
      
      if (!selectedCompany) {
        throw new Error('선택한 업체 정보를 찾을 수 없습니다.');
      }

      // 토큰 발급
      const response = await fetchToken(email, password, company, 'IT');
      
      if (!response.token) {
        throw new Error('로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.');
      }
      
      // 토큰 디코딩 및 정보 저장
      try {
        const decoded = jwtDecode(response.token) as DecodedToken;
        localStorage.setItem('token', response.token);
        localStorage.setItem('UserNo', decoded.UserNo);
        localStorage.setItem('CompanyNo', decoded.CompanyNo);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('expirationTime', response.expirationTime);
        localStorage.setItem('isLoggedIn', 'true');
        
        // 로그인 성공 후 유저 정보 가져오기
        await fetchUserInfo();
        await fetchCompanyInfo();
      } catch (decodeError) {
        console.error('Token decode error:', decodeError);
        setError('Invalid token format');
      }
      
      // 내일정표 페이지로 이동
      navigate('/itinerary-list', { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      component="main" 
      maxWidth="xs" 
      sx={{
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center',
        py: 3
      }}
    >
      <Box 
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper 
          component="form" 
          onSubmit={handleLogin} 
          sx={{ 
            mt: 2, 
            p: 3,  
            width: '100%', 
            minHeight: { xs: 'auto', sm: '620px' },
            display: 'flex', 
            flexDirection: 'column',
            borderRadius: '10px'
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 2
          }}>
            <Typography component="h1" variant="h5">
              로그인
            </Typography>
          </Box>

          <TextField
            placeholder='아이디'
            margin="normal"
            required
            fullWidth
            id="email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ 
              backgroundColor: 'background.paper', 
              mt: 6,
              '& .MuiInputBase-input': {
                '-webkit-text-fill-color': 'inherit',
                '-webkit-box-shadow': '0 0 0px 1000px white inset'
              }
            }}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            placeholder="비밀번호"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ 
              backgroundColor: 'background.paper',
              '& .MuiInputBase-input': {
                '-webkit-text-fill-color': 'inherit',
                '-webkit-box-shadow': '0 0 0px 1000px white inset'
              }
            }}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>로그인 업체</InputLabel>
            <Select
              value={company}
              label="로그인 업체"
              onChange={handleCompanyChange}
              sx={{ backgroundColor: 'background.paper' }}
              disabled={loading}
              onOpen={handleCompanySelectOpen}
              open={selectOpen}
              onClose={() => setSelectOpen(false)}
            >
              <MenuItem sx={{ color: 'text.disabled' }} value="">선택하세요</MenuItem>
              {companies.map((comp) => (
                <MenuItem key={comp.cust_no} value={comp.cust_no}>
                  {comp.cust_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 'auto', mb: 3,  width: '100%' }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mb: 2, color: 'primary.contrastText' }}
              disabled={loading || !company}
            >
              {loading ? '로그인 중...' : '로그인'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/register')}
              sx={{ mb: 2 }}
              disabled={loading}
              startIcon={<PersonAddIcon />}
            >
              회원가입으로 이동
            </Button>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 0.5
            }}>
              <LockResetIcon sx={{ color: 'text.secondary', fontSize: '1rem' }} />
              <Typography
                variant="body2"
                sx={{ 
                  color: 'text.secondary',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                비밀번호 초기화
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Login;
